import React, { useEffect, useState, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import styles from "./ManageEnterprise.module.css";
import { callApiWithOptions, HttpMethod } from "src/services/apiService";
import Loading from "src/components/Loading";
import ConfirmationModal from "src/components/ConfirmationModal";
import SaveInProgress from "src/components/SaveInProgress";
import EmailTextarea from "src/components/EmailTextarea";
import { EnterpriseResponse } from "src/interfaces/LogoCall/IEnterprise";
import { NORTH_AMERICAN_REGIONS } from "src/data/us-states-and-can-provinces";
import { COUNTRIES } from "src/data/countries";
import { SlSelect, SlOption } from "@shoelace-style/shoelace/dist/react";
import BCIDDocumentUpload from "src/components/LogoCall/BCIDDocumentUpload";

interface OSP {
  originatingServiceProviderId: string;
  isActive: boolean;
  ospName: string;
  spcs: string;
  updatedDate: string;
}

interface EnterpriseDetails {
  legalName: string;
  ein: string;
  primaryPhoneNumber: string;
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  countryCode: string;
  industry: string;
  employeeCount: number;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhoneNumber: string;
  dunsNumber: string;
  url: string;
  dba: string;
  callsPerMonth: number;
  industryId: number;
  ospIds: number[];
  orgType: string;
  // Legal Entity new fields
  stateOfIncorporation: string;
  primaryContactTitle: string;
  businessActivityDescription: string;
  callingUseCase: string;
  insuranceLicense: string;
  // OSP Section
  localPresenceType: string;
  multipleNumbersInAreaCode: string;
  multipleTollFreeNumbers: string;
  automatedNumberRotation: string;
  dialingPlatform: string;
  monthlyCallVolume: number;
  isBpo: boolean;
  bpoTnMappingDetails: string;
  // Compliance & Consent
  consentMethod: string;
  optOutProcess: string;
  consentAndDncScrub: string;
  scrubsReassignedNumbers: boolean;
  hasTcpaLawsuits: string;
  spamCallReason: string;
  noIllegalCallingConfirmation: boolean;
  bcidGuidelinesAgreement: boolean;
  thirdPartyLeadsValidation: string;
  // Call Authentication & Technical Controls
  stirShakenCompliance: string;
  usesPrerecordedOrAiVoice: string;
  // Risk & Behavioral Review
  fraudSafeguards: string;
  callCampaignMonitoring: string;
  complaintRates: string;
  // Financial / Credibility Information
  businessReferences: string;
  bankingReference: string;
  // Consumer Contact Channels
  consumerComplaintEmail: string;
  consumerComplaintPhone: string;
  privacyPolicyUrl: string;
  // Ongoing Obligations
  mustUpdateInfoChanges: boolean;
  brandingChangesRequireApproval: boolean;
  subjectToRevettingAndAudits: boolean;
  // Notifications
  notificationEmails: string;
  // Vetting Outcome
  vettingStatus: string;
  vettingUnsuccessfulReason: string;
}

const formatEIN = (value: string) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, "");

  // Format as XX-XXXXXXX
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`;
};

const unformatEIN = (value: string) => {
  return value.replace(/\D/g, "");
};

const FIELD_TAB_MAP: Record<
  string,
  { tab: string; tabLabel: string; fieldLabel: string }
> = {
  // Legal tab
  legalName: { tab: "legal", tabLabel: "Legal", fieldLabel: "Legal Name" },
  ein: { tab: "legal", tabLabel: "Legal", fieldLabel: "EIN" },
  orgType: { tab: "legal", tabLabel: "Legal", fieldLabel: "Organization Type" },
  state: { tab: "legal", tabLabel: "Legal", fieldLabel: "State / Province" },
  countryCode: { tab: "legal", tabLabel: "Legal", fieldLabel: "Country" },
  streetAddress: {
    tab: "legal",
    tabLabel: "Legal",
    fieldLabel: "Street Address",
  },
  city: { tab: "legal", tabLabel: "Legal", fieldLabel: "City" },
  zip: { tab: "legal", tabLabel: "Legal", fieldLabel: "ZIP Code" },
  dunsNumber: { tab: "legal", tabLabel: "Legal", fieldLabel: "DUNS Number" },
  // Business tab
  employeeCount: {
    tab: "business",
    tabLabel: "Business",
    fieldLabel: "Employee Count",
  },
  callsPerMonth: {
    tab: "business",
    tabLabel: "Business",
    fieldLabel: "Calls Per Month",
  },
  url: { tab: "business", tabLabel: "Business", fieldLabel: "Website URL" },
  // Contacts tab
  primaryContactEmail: {
    tab: "contacts",
    tabLabel: "Contacts",
    fieldLabel: "Primary Contact Email",
  },
  primaryContactPhoneNumber: {
    tab: "contacts",
    tabLabel: "Contacts",
    fieldLabel: "Primary Contact Phone",
  },
  // Service Providers tab
  ospIds: {
    tab: "osp",
    tabLabel: "Service Providers",
    fieldLabel: "Operating Service Providers",
  },
};

const ManageEnterprise: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitFeedback, setSubmitFeedback] = useState<React.ReactNode | null>(
    null,
  );
  const [enterpriseData, setEnterpriseData] =
    useState<EnterpriseResponse | null>(null);
  const [availableOSPs, setAvailableOSPs] = useState<OSP[]>([]);
  const [industries, setIndustries] = useState<
    { text: string; value: string }[]
  >([]);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [savingAction, setSavingAction] = useState<string | null>(null);
  const tabOrder = [
    "legal",
    "business",
    "contacts",
    "osp",
    "compliance",
    "auth",
    "financial",
    "obligations",
  ];
  const [activeTab, setActiveTab] = useState("legal");
  const [draftConfirm, setDraftConfirm] = useState<{
    message: string;
    onConfirm: () => void;
  } | null>(null);
  const [unsavedTabTarget, setUnsavedTabTarget] = useState<string | null>(null);

  const [isFormDirty, setIsFormDirty] = useState(false);

  const formMethods = useForm<EnterpriseDetails>();
  const {
    register,
    formState: { errors },
    control,
    reset,
    trigger,
    getValues,
  } = formMethods;

  const requestTabChange = useCallback(
    (nextTab: string) => {
      if (nextTab === activeTab) return;
      if (isFormDirty) {
        setUnsavedTabTarget(nextTab);
        return;
      }
      setActiveTab(nextTab);
    },
    [activeTab, isFormDirty],
  );

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);
      setError(null);

      // Load OSPs
      const ospResponse = await callApiWithOptions({
        endpoint: "bcid/enterprise/osps",
        method: HttpMethod.GET,
      });
      // Filter for active OSPs only
      setAvailableOSPs(ospResponse.filter((osp: OSP) => osp.isActive));

      // Load enterprise data
      const response = await callApiWithOptions({
        endpoint: "bcid/enterprise",
        method: HttpMethod.GET,
        allowedErrorStatus: [404],
      });

      if (response.status === 404) {
        setError(
          "There was an issue loading the enterprise data. Please try again.",
        );
        return;
      }

      if (response.industries) {
        setIndustries(response.industries);
      }

      setEnterpriseData(response);

      const dto = response.dto || {};
      // Map the nested legalEntity data to form fields
      if (dto.legalEntity) {
        reset({
          legalName: dto.legalEntity.legalName,
          ein: dto.legalEntity.ein ? formatEIN(dto.legalEntity.ein) : "",
          primaryPhoneNumber: dto.legalEntity.primaryPhoneNumber,
          streetAddress: dto.legalEntity.streetAddress,
          city: dto.legalEntity.city,
          state: dto.legalEntity.state,
          zip: dto.legalEntity.zip?.trim(), // Remove trailing spaces
          countryCode: dto.legalEntity.countryCode?.trim(),
          industry: dto.legalEntity.industry || "",
          employeeCount: dto.legalEntity.employeeCount,
          primaryContactName: dto.legalEntity.primaryContactName,
          primaryContactEmail: dto.legalEntity.primaryContactEmail,
          primaryContactPhoneNumber: dto.legalEntity.primaryContactPhoneNumber,
          dunsNumber: dto.legalEntity.dunsNumber || "",
          url: dto.legalEntity.url,
          dba: dto.legalEntity.dba,
          callsPerMonth: dto.legalEntity.callsPerMonth,
          industryId: dto.legalEntity.industryId,
          ospIds: dto.ospIds || [],
          orgType: dto.orgType || "",
          // Legal Entity new fields
          stateOfIncorporation: dto.legalEntity.stateOfIncorporation || "",
          primaryContactTitle: dto.legalEntity.primaryContactTitle || "",
          businessActivityDescription:
            dto.legalEntity.businessActivityDescription || "",
          callingUseCase: dto.legalEntity.callingUseCase || "",
          insuranceLicense: dto.legalEntity.insuranceLicense || "",
          // Enterprise fields
          localPresenceType: dto.localPresenceType || "",
          multipleNumbersInAreaCode: dto.multipleNumbersInAreaCode || "",
          multipleTollFreeNumbers: dto.multipleTollFreeNumbers || "",
          automatedNumberRotation: dto.automatedNumberRotation || "",
          dialingPlatform: dto.dialingPlatform || "",
          monthlyCallVolume: dto.monthlyCallVolume || 0,
          isBpo: dto.isBpo || false,
          bpoTnMappingDetails: dto.bpoTnMappingDetails || "",
          consentMethod: dto.consentMethod || "",
          optOutProcess: dto.optOutProcess || "",
          consentAndDncScrub: dto.consentAndDncScrub || "",
          scrubsReassignedNumbers: dto.scrubsReassignedNumbers || false,
          hasTcpaLawsuits: dto.hasTcpaLawsuits || "",
          spamCallReason: dto.spamCallReason || "",
          noIllegalCallingConfirmation:
            dto.noIllegalCallingConfirmation || false,
          bcidGuidelinesAgreement: dto.bcidGuidelinesAgreement || false,
          thirdPartyLeadsValidation: dto.thirdPartyLeadsValidation || "",
          stirShakenCompliance: dto.stirShakenCompliance || "",
          usesPrerecordedOrAiVoice: dto.usesPrerecordedOrAiVoice || "",
          fraudSafeguards: dto.fraudSafeguards || "",
          callCampaignMonitoring: dto.callCampaignMonitoring || "",
          complaintRates: dto.complaintRates || "",
          businessReferences: dto.businessReferences || "",
          bankingReference: dto.bankingReference || "",
          consumerComplaintEmail: dto.consumerComplaintEmail || "",
          consumerComplaintPhone: dto.consumerComplaintPhone || "",
          privacyPolicyUrl: dto.privacyPolicyUrl || "",
          mustUpdateInfoChanges: dto.mustUpdateInfoChanges || false,
          brandingChangesRequireApproval:
            dto.brandingChangesRequireApproval || false,
          subjectToRevettingAndAudits: dto.subjectToRevettingAndAudits || false,
          notificationEmails: dto.notificationEmails || "",
          vettingStatus: dto.vettingStatus || "",
          vettingUnsuccessfulReason: dto.vettingUnsuccessfulReason || "",
        });
        setIsFormDirty(false);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while loading enterprise data",
      );
      console.error("Error loading enterprise data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [reset]);

  const getDraftConfirmMessage = (): string | null => {
    const status = Number(enterpriseData?.dto?.status);
    // DRAFT (-2) or WITHDRAWN (-1): no confirmation needed
    if (status === -1 || status === -2) {
      return null;
    }
    if (status === 0 || status === 1) {
      return "This will create a new version. Your current submission will not be affected.";
    }
    return null;
  };

  const onSave = async (isDraft: boolean): Promise<boolean> => {
    setSubmitFeedback(null);

    if (!isDraft) {
      const isValid = await trigger(undefined, { shouldFocus: false });
      if (!isValid) {
        const currentErrors = formMethods.formState.errors;
        const errorFields = Object.keys(currentErrors);
        const tabErrors: Record<string, string[]> = {};
        for (const fieldName of errorFields) {
          const mapping = FIELD_TAB_MAP[fieldName];
          if (mapping) {
            if (!tabErrors[mapping.tabLabel]) {
              tabErrors[mapping.tabLabel] = [];
            }
            tabErrors[mapping.tabLabel].push(mapping.fieldLabel);
          }
        }

        const errorList = Object.entries(tabErrors).map(
          ([tabLabel, fields]) => (
            <div key={tabLabel}>
              <strong>{tabLabel}:</strong> {fields.join(", ")}
            </div>
          ),
        );

        setSubmitFeedback(
          <div>
            <div>Some required fields are missing or invalid:</div>
            {errorList.length > 0 ? (
              errorList
            ) : (
              <div>Check each tab for fields marked in red.</div>
            )}
          </div>,
        );
        return false;
      }
    }

    const data = getValues();
    setSaveInProgress(true);
    console.log("Form submitted with data:", data);
    try {
      if (!enterpriseData?.dto) {
        setSubmitFeedback(
          "Enterprise data is missing. Refresh the page and try again.",
        );
        console.error("Enterprise data is missing.");
        return false;
      }

      const updatePayload = {
        isDraft,
        enterpriseId: enterpriseData?.dto.enterpriseId,
        orgType: data.orgType,
        legalEntity: {
          legalEntityId: enterpriseData?.dto.legalEntity.legalEntityId,
          legalName: data.legalName,
          ein: unformatEIN(data.ein),
          primaryPhoneNumber: data.primaryPhoneNumber || null,
          streetAddress: data.streetAddress || null,
          city: data.city || null,
          state: data.state || null,
          zip: data.zip || null,
          countryCode: data.countryCode || null,
          employeeCount: data.employeeCount || null,
          primaryContactName: data.primaryContactName || null,
          primaryContactEmail: data.primaryContactEmail || null,
          primaryContactPhoneNumber: data.primaryContactPhoneNumber || null,
          dunsNumber: data.dunsNumber ? data.dunsNumber.trim() : null,
          url: data.url || null,
          dba: data.dba || null,
          callsPerMonth: data.callsPerMonth || null,
          industryId: data.industryId ? Number(data.industryId) : null,
          stateOfIncorporation: data.stateOfIncorporation || null,
          primaryContactTitle: data.primaryContactTitle || null,
          businessActivityDescription: data.businessActivityDescription || null,
          callingUseCase: data.callingUseCase || null,
          insuranceLicense: data.insuranceLicense || null,
        },
        ospIds: data.ospIds ? data.ospIds.map(String) : [],
        // OSP Section
        localPresenceType: data.localPresenceType || null,
        multipleNumbersInAreaCode: data.multipleNumbersInAreaCode || null,
        multipleTollFreeNumbers: data.multipleTollFreeNumbers || null,
        automatedNumberRotation: data.automatedNumberRotation || null,
        dialingPlatform: data.dialingPlatform || null,
        monthlyCallVolume: data.monthlyCallVolume ?? null,
        isBpo: data.isBpo ?? null,
        bpoTnMappingDetails: data.bpoTnMappingDetails || null,
        consentMethod: data.consentMethod || null,
        optOutProcess: data.optOutProcess || null,
        consentAndDncScrub: data.consentAndDncScrub || null,
        scrubsReassignedNumbers: data.scrubsReassignedNumbers ?? null,
        hasTcpaLawsuits: data.hasTcpaLawsuits || null,
        spamCallReason: data.spamCallReason || null,
        noIllegalCallingConfirmation: data.noIllegalCallingConfirmation ?? null,
        bcidGuidelinesAgreement: data.bcidGuidelinesAgreement ?? null,
        thirdPartyLeadsValidation: data.thirdPartyLeadsValidation || null,
        stirShakenCompliance: data.stirShakenCompliance || null,
        usesPrerecordedOrAiVoice: data.usesPrerecordedOrAiVoice || null,
        fraudSafeguards: data.fraudSafeguards || null,
        callCampaignMonitoring: data.callCampaignMonitoring || null,
        complaintRates: data.complaintRates || null,
        businessReferences: data.businessReferences || null,
        bankingReference: data.bankingReference || null,
        consumerComplaintEmail: data.consumerComplaintEmail || null,
        consumerComplaintPhone: data.consumerComplaintPhone || null,
        privacyPolicyUrl: data.privacyPolicyUrl || null,
        mustUpdateInfoChanges: data.mustUpdateInfoChanges ?? null,
        brandingChangesRequireApproval:
          data.brandingChangesRequireApproval ?? null,
        subjectToRevettingAndAudits: data.subjectToRevettingAndAudits ?? null,
        notificationEmails: data.notificationEmails || null,
        vettingStatus: data.vettingStatus || null,
        vettingUnsuccessfulReason: data.vettingUnsuccessfulReason || null,
      };

      console.log("Submitting Payload:", updatePayload);

      // Call API with the formatted data
      const enterpriseSaveResponse = await callApiWithOptions({
        endpoint: `bcid/enterprise/save`,
        method: HttpMethod.POST,
        body: updatePayload, // Ensure JSON format
      });
      await loadData(false);
      if (!isDraft) {
        setActiveTab("legal");
      }
      console.log("Enterprise updated successfully:", enterpriseSaveResponse);
      setSubmitFeedback(null);
      return true;
    } catch (error) {
      console.error("Failed to update enterprise:", error);
      const err = error as Error & {
        data?: { error?: string; message?: string };
      };
      const apiMsg =
        err.data?.error || err.data?.message || err.message || null;
      setSubmitFeedback(
        apiMsg ?? "Could not save your enterprise. Please try again.",
      );
      return false;
    } finally {
      setSaveInProgress(false);
      setSavingAction(null);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {submitFeedback && (
        <div
          className="alert alert-warning alert-dismissible fade show"
          role="alert"
        >
          {submitFeedback}
          <button
            type="button"
            className="btn-close"
            aria-label="Dismiss"
            onClick={() => setSubmitFeedback(null)}
          />
        </div>
      )}

      <form
        onSubmit={(e) => e.preventDefault()}
        onChange={() => setIsFormDirty(true)}
      >
        <p>
          Please fill out the enterprise information below. Fields marked with
          an asterisk (*) are required for submission. You can save a draft at
          any time.
        </p>

        {/* Active in BCID indicator */}
        {enterpriseData?.dto?.isActiveInBCID && (
          <div className="alert alert-success d-flex align-items-center mb-3" role="alert">
            <i className="bi bi-check-circle-fill me-2"></i>
            <strong>Active in BCID</strong>
            {enterpriseData.dto.isPaused && <span className="ms-1">(Currently Paused)</span>}
            {enterpriseData.dto.status !== 1 && !enterpriseData.dto.isPaused && (
              <span className="ms-1 text-muted">&mdash; You are editing a new draft. The active version is unaffected.</span>
            )}
          </div>
        )}


        {/* Tab Navigation */}
        <ul className="nav nav-tabs mb-3" style={{ gap: "4px" }}>
          {[
            { key: "legal", label: "Legal" },
            { key: "business", label: "Business" },
            { key: "contacts", label: "Contacts" },
            { key: "osp", label: "Service Providers" },
            { key: "compliance", label: "Compliance" },
            { key: "auth", label: "Authentication & Risk" },
            { key: "financial", label: "Financial" },
            { key: "obligations", label: "Obligations" },
          ].map((tab, index) => {
            const tabHasErrors = Object.keys(errors).some(
              (fieldName) => FIELD_TAB_MAP[fieldName]?.tab === tab.key,
            );
            return (
              <li className="nav-item" key={tab.key}>
                <button
                  type="button"
                  className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
                  onClick={() => requestTabChange(tab.key)}
                >
                  <span
                    className={`${styles.tabNumber} ${
                      tabHasErrors
                        ? styles.tabNumberError
                        : activeTab === tab.key
                          ? styles.tabNumberActive
                          : ""
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span className={tabHasErrors ? styles.tabLabelError : ""}>
                    {tab.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <div style={{ display: activeTab === "legal" ? "block" : "none" }}>
          <div className="card">
            <div className="card-header">
              <i className="bi bi-building me-2"></i>
              Legal Information
              <span
                className={`badge rounded-pill ms-2 ${
                  enterpriseData?.dto.status === -4
                    ? "bg-dark text-light"
                    : enterpriseData?.dto.isPaused
                      ? "bg-warning text-dark"
                      : enterpriseData?.dto.status === -2
                        ? "bg-secondary text-light"
                        : enterpriseData?.dto.status === 0
                          ? "bg-primary text-light"
                          : enterpriseData?.dto.status === 1
                            ? "bg-success text-light"
                            : "bg-danger text-light"
                }`}
              >
                {(enterpriseData?.dto.status ?? 0) > 1
                  ? "Not Approved"
                  : enterpriseData?.dto.statusName}
              </span>
              {(enterpriseData?.dto.status ?? 0) > 1 &&
                enterpriseData?.dto.statusName && (
                  <span className="ms-2 small text-danger">
                    {enterpriseData.dto.statusName}
                  </span>
                )}
            </div>
            <div className="card-body">
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label htmlFor="legalName" className="form-label">
                    Legal Name*
                  </label>
                  <input
                    id="legalName"
                    type="text"
                    className={`form-control ${errors.legalName ? "is-invalid" : ""}`}
                    {...register("legalName", {
                      required: "Legal name is required",
                    })}
                  />
                  {errors.legalName && (
                    <div className="invalid-feedback">
                      {errors.legalName.message}
                    </div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="ein" className="form-label">
                    EIN*
                  </label>
                  <Controller
                    name="ein"
                    control={control}
                    rules={{
                      required: "EIN is required",
                      pattern: {
                        value: /^\d{2}-\d{7}$/,
                        message: "EIN must be in format XX-XXXXXXX",
                      },
                      validate: (value) =>
                        value.trim() !== "" || "EIN cannot be empty",
                    }}
                    render={({ field }) => (
                      <input
                        id="ein"
                        type="text"
                        className={`form-control ${errors.ein ? "is-invalid" : ""}`}
                        maxLength={10}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={(e) =>
                          field.onChange(formatEIN(e.target.value))
                        } // Format onBlur
                        placeholder="XX-XXXXXXX"
                      />
                    )}
                  />
                  {errors.ein && (
                    <div className="invalid-feedback">{errors.ein.message}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="dba" className="form-label">
                    DBA (Doing Business As)
                  </label>
                  <input
                    id="dba"
                    type="text"
                    className="form-control"
                    {...register("dba")}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="dunsNumber" className="form-label">
                    DUNS Number
                  </label>
                  <input
                    id="dunsNumber"
                    type="text"
                    className={`form-control ${errors.dunsNumber ? "is-invalid" : ""}`}
                    {...register("dunsNumber", {
                      pattern: {
                        value: /^\d{9}$/,
                        message: "DUNS number must be 9 digits",
                      },
                      validate: (value) =>
                        value.trim() === "" ||
                        /^\d{9}$/.test(value) ||
                        "DUNS must be 9 digits",
                    })}
                  />
                  {errors.dunsNumber && (
                    <div className="invalid-feedback">
                      {errors.dunsNumber.message}
                    </div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="orgType" className="form-label">
                    Organization Type*
                  </label>
                  <select
                    id="orgType"
                    className={`form-select ${errors.orgType ? "is-invalid" : ""}`}
                    {...register("orgType", {
                      required: "Organization type is required",
                    })}
                  >
                    <option value="">--Select Organization Type--</option>
                    <option value="A">Commercial Enterprise</option>
                    <option value="B">Government/Public Service</option>
                    <option value="C">Public Charity/Non-profit</option>
                  </select>
                  {errors.orgType && (
                    <div className="invalid-feedback">
                      {errors.orgType.message}
                    </div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="stateOfIncorporation" className="form-label">
                    State of Incorporation
                  </label>
                  <select
                    id="stateOfIncorporation"
                    className="form-select"
                    {...register("stateOfIncorporation")}
                  >
                    <option value="">--Select State--</option>
                    {NORTH_AMERICAN_REGIONS.slice(1).map((region) => (
                      <option key={region.value} value={region.value}>
                        {region.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="card mt-3">
            <div className="card-header">
              <i className="bi bi-geo-alt me-2"></i>
              Address Information
            </div>
            <div className="card-body">
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label htmlFor="streetAddress" className="form-label">
                    Street Address*
                  </label>
                  <input
                    id="streetAddress"
                    type="text"
                    className={`form-control ${errors.streetAddress ? "is-invalid" : ""}`}
                    {...register("streetAddress", {
                      required: "Street address is required",
                    })}
                  />
                  {errors.streetAddress && (
                    <div className="invalid-feedback">
                      {errors.streetAddress.message}
                    </div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="city" className="form-label">
                    City*
                  </label>
                  <input
                    id="city"
                    type="text"
                    className={`form-control ${errors.city ? "is-invalid" : ""}`}
                    {...register("city", {
                      required: "City is required",
                    })}
                  />
                  {errors.city && (
                    <div className="invalid-feedback">
                      {errors.city.message}
                    </div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="state" className="form-label">
                    State / Province*
                  </label>
                  <select
                    id="state"
                    className={`form-select ${errors.state ? "is-invalid" : ""}`}
                    {...register("state", { required: "State is required" })}
                  >
                    <option value="">--Select State/Province--</option>
                    {NORTH_AMERICAN_REGIONS.slice(1).map((region) => (
                      <option key={region.value} value={region.value}>
                        {region.label}
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <div className="invalid-feedback">
                      {errors.state.message}
                    </div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="zip" className="form-label">
                    ZIP/Postal Code*
                  </label>
                  <input
                    id="zip"
                    type="text"
                    className={`form-control ${errors.zip ? "is-invalid" : ""}`}
                    {...register("zip", {
                      required: "ZIP/Postal code is required",
                      validate: (value) => {
                        const country = getValues("countryCode");
                        const normalizedValue = value.trim();
                        if (country === "CA") {
                          return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(normalizedValue.toUpperCase()) || "Invalid postal code format";
                        }
                        return /^\d{5}(-\d{4})?$/.test(normalizedValue) || "Invalid ZIP code format";
                      },
                    })}
                  />
                  {errors.zip && (
                    <div className="invalid-feedback">{errors.zip.message}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="countryCode" className="form-label">
                    Country*
                  </label>
                  <select
                    id="countryCode"
                    className={`form-select ${errors.countryCode ? "is-invalid" : ""}`}
                    {...register("countryCode", {
                      required: "Country is required",
                    })}
                  >
                    {COUNTRIES.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {errors.countryCode && (
                    <div className="invalid-feedback">
                      {errors.countryCode.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: activeTab === "business" ? "block" : "none" }}>
          <div className="card">
            <div className="card-header">
              <i className="bi bi-briefcase me-2"></i>
              Business Information
            </div>
            <div className="card-body">
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label htmlFor="industryId" className="form-label">
                    Industry
                  </label>
                  <Controller
                    name="industryId"
                    control={control}
                    render={({ field }) => (
                      <select
                        id="industryId"
                        className={`form-select ${errors.industryId ? "is-invalid" : ""}`}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      >
                        <option value="">--Select Industry--</option>{" "}
                        {industries.map((industry) => (
                          <option key={industry.value} value={industry.value}>
                            {industry.text}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="employeeCount" className="form-label">
                    Employee Count
                  </label>
                  <input
                    id="employeeCount"
                    type="number"
                    className={`form-control ${errors.employeeCount ? "is-invalid" : ""}`}
                    {...register("employeeCount", {
                      valueAsNumber: true,
                      min: {
                        value: 0,
                        message: "Employee count must be positive",
                      },
                    })}
                  />
                  {errors.employeeCount && (
                    <div className="invalid-feedback">
                      {errors.employeeCount.message}
                    </div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="callsPerMonth" className="form-label">
                    Calls Per Month
                  </label>
                  <input
                    id="callsPerMonth"
                    type="number"
                    className={`form-control ${errors.callsPerMonth ? "is-invalid" : ""}`}
                    {...register("callsPerMonth", {
                      valueAsNumber: true,
                      min: {
                        value: 0,
                        message: "Calls per month must be positive",
                      },
                    })}
                  />
                  {errors.callsPerMonth && (
                    <div className="invalid-feedback">
                      {errors.callsPerMonth.message}
                    </div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="url" className="form-label">
                    Website URL
                  </label>
                  <input
                    id="url"
                    type="url"
                    className={`form-control ${errors.url ? "is-invalid" : ""}`}
                    {...register("url", {
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: "Must be a valid URL",
                      },
                    })}
                  />
                  {errors.url && (
                    <div className="invalid-feedback">{errors.url.message}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card mt-3">
            <div className="card-header">
              <i className="bi bi-file-text me-2"></i>
              Business Profile
            </div>
            <div className="card-body">
              <div className={styles.formGrid}>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label
                    htmlFor="businessActivityDescription"
                    className="form-label"
                  >
                    Description of Business Activities
                  </label>
                  <textarea
                    id="businessActivityDescription"
                    className="form-control"
                    rows={3}
                    {...register("businessActivityDescription")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="callingUseCase" className="form-label">
                    Use Case for Calling (why are you calling consumers?)
                  </label>
                  <textarea
                    id="callingUseCase"
                    className="form-control"
                    rows={3}
                    {...register("callingUseCase")}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="insuranceLicense" className="form-label">
                    Insurance License (if applicable)
                  </label>
                  <input
                    id="insuranceLicense"
                    type="text"
                    className="form-control"
                    {...register("insuranceLicense")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: activeTab === "contacts" ? "block" : "none" }}>
          <div className="card mt-3">
            <div className="card-header">
              <i className="bi bi-person me-2"></i>
              Primary Contact Information
            </div>
            <div className="card-body">
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label htmlFor="primaryContactName">Contact Name</label>
                  <input
                    className={`form-control`}
                    id="primaryContactName"
                    type="text"
                    {...register("primaryContactName")}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="primaryContactTitle">Title / Role</label>
                  <input
                    className="form-control"
                    id="primaryContactTitle"
                    type="text"
                    {...register("primaryContactTitle")}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="primaryContactEmail">Contact Email</label>
                  <input
                    id="primaryContactEmail"
                    className={`form-control`}
                    type="email"
                    {...register("primaryContactEmail", {
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                  />
                  {errors.primaryContactEmail && (
                    <span className={styles.error}>
                      {errors.primaryContactEmail.message}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="primaryContactPhoneNumber">
                    Contact Phone
                  </label>
                  <input
                    className={`form-control`}
                    id="primaryContactPhoneNumber"
                    type="tel"
                    {...register("primaryContactPhoneNumber", {
                      pattern: {
                        value: /^\+?[\d\s-]+$/,
                        message: "Invalid phone number",
                      },
                    })}
                  />
                  {errors.primaryContactPhoneNumber && (
                    <span className={styles.error}>
                      {errors.primaryContactPhoneNumber.message}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: activeTab === "osp" ? "block" : "none" }}>
          <div className="card mt-3">
            <div className="card-header">
              <i className="bi bi-telephone me-2"></i>
              Operating Service Providers
            </div>
            <div className="card-body">
              <div className={styles.formGroup}>
                <label className="form-label">
                  Operating Service Providers*
                </label>
                <Controller
                  name="ospIds"
                  control={control}
                  rules={{ required: "Please select at least one OSP" }}
                  render={({ field }) => (
                    <SlSelect
                      multiple
                      value={(field.value || []).map(String)}
                      onSlChange={(e) => {
                        const target = e.target as any;
                        if (target) {
                          const selectedValues = Array.isArray(target.value)
                            ? target.value
                            : [target.value];
                          field.onChange(selectedValues);
                          setIsFormDirty(true);
                        }
                      }}
                      className={`w-100 ${styles.ospSelect}`}
                      placeholder="--Select Operating Service Provider--"
                    >
                      {availableOSPs.map((osp) => (
                        <SlOption
                          key={osp.originatingServiceProviderId}
                          value={osp.originatingServiceProviderId}
                        >
                          {osp.ospName}
                        </SlOption>
                      ))}
                    </SlSelect>
                  )}
                />

                {errors.ospIds && (
                  <div className="invalid-feedback d-block">
                    {errors.ospIds.message}
                  </div>
                )}
              </div>
              <div className={`${styles.formGrid} mt-3`}>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="localPresenceType" className="form-label">
                    Do you use Local Presence, Toll Free, or your own area code
                    only?
                  </label>
                  <input
                    id="localPresenceType"
                    type="text"
                    className="form-control"
                    {...register("localPresenceType")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label
                    htmlFor="multipleNumbersInAreaCode"
                    className="form-label"
                  >
                    Do you have multiple phone numbers in one area code? If so,
                    why?
                  </label>
                  <textarea
                    id="multipleNumbersInAreaCode"
                    className="form-control"
                    rows={2}
                    {...register("multipleNumbersInAreaCode")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label
                    htmlFor="multipleTollFreeNumbers"
                    className="form-label"
                  >
                    Do you have multiple Toll-Free Numbers? If so, why?
                  </label>
                  <textarea
                    id="multipleTollFreeNumbers"
                    className="form-control"
                    rows={2}
                    {...register("multipleTollFreeNumbers")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label
                    htmlFor="automatedNumberRotation"
                    className="form-label"
                  >
                    Do you have automated number rotation in place? If so, why?
                  </label>
                  <textarea
                    id="automatedNumberRotation"
                    className="form-control"
                    rows={2}
                    {...register("automatedNumberRotation")}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="dialingPlatform" className="form-label">
                    What dialing platform do you use?
                  </label>
                  <input
                    id="dialingPlatform"
                    type="text"
                    className="form-control"
                    {...register("dialingPlatform")}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="monthlyCallVolume" className="form-label">
                    Monthly Call Volume
                  </label>
                  <input
                    id="monthlyCallVolume"
                    type="number"
                    className="form-control"
                    {...register("monthlyCallVolume", { valueAsNumber: true })}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="bpoTnMappingDetails" className="form-label">
                    Are you a BPO? Provide TN mapping to customers
                  </label>
                  <textarea
                    id="bpoTnMappingDetails"
                    className="form-control"
                    rows={2}
                    {...register("bpoTnMappingDetails")}
                  />
                </div>
              </div>
              <div className="mt-3">
                <BCIDDocumentUpload
                  entityId={enterpriseData?.dto?.enterpriseId || null}
                  documentType="CallDetailRecords"
                  label="Provide 30 Days Call Detail Records"
                />
                <BCIDDocumentUpload
                  entityId={enterpriseData?.dto?.enterpriseId || null}
                  documentType="LetterOfAuthorization"
                  label="Provide Letter of Authorization (LOA) if applicable"
                />
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: activeTab === "compliance" ? "block" : "none" }}>
          <div className="card mt-3">
            <div className="card-header">
              <i className="bi bi-shield-check me-2"></i>
              Compliance & Consent
            </div>
            <div className="card-body">
              <div className={styles.formGrid}>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="consentMethod" className="form-label">
                    How do you obtain consent?
                  </label>
                  <textarea
                    id="consentMethod"
                    className="form-control"
                    rows={3}
                    {...register("consentMethod")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="optOutProcess" className="form-label">
                    Describe opt-out process
                  </label>
                  <textarea
                    id="optOutProcess"
                    className="form-control"
                    rows={3}
                    {...register("optOutProcess")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="consentAndDncScrub" className="form-label">
                    Do you rely on consent and scrub DNC lists?
                  </label>
                  <textarea
                    id="consentAndDncScrub"
                    className="form-control"
                    rows={2}
                    {...register("consentAndDncScrub")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <div className="form-check mt-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="scrubsReassignedNumbers"
                      {...register("scrubsReassignedNumbers")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="scrubsReassignedNumbers"
                    >
                      Do you scrub reassigned numbers?
                    </label>
                  </div>
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="hasTcpaLawsuits" className="form-label">
                    Any TCPA lawsuits?
                  </label>
                  <textarea
                    id="hasTcpaLawsuits"
                    className="form-control"
                    rows={2}
                    {...register("hasTcpaLawsuits")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="spamCallReason" className="form-label">
                    Why might consumers mark your calls as spam?
                  </label>
                  <textarea
                    id="spamCallReason"
                    className="form-control"
                    rows={2}
                    {...register("spamCallReason")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label
                    htmlFor="thirdPartyLeadsValidation"
                    className="form-label"
                  >
                    Use of third-party leads and validation process
                  </label>
                  <textarea
                    id="thirdPartyLeadsValidation"
                    className="form-control"
                    rows={2}
                    {...register("thirdPartyLeadsValidation")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <div className="form-check mt-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="noIllegalCallingConfirmation"
                      {...register("noIllegalCallingConfirmation")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="noIllegalCallingConfirmation"
                    >
                      Confirmation: not engaged in illegal calling activity
                    </label>
                  </div>
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <div className="form-check mt-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="bcidGuidelinesAgreement"
                      {...register("bcidGuidelinesAgreement")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="bcidGuidelinesAgreement"
                    >
                      Agreement to follow BCID guidelines and content rules
                    </label>
                  </div>
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <BCIDDocumentUpload
                    entityId={enterpriseData?.dto?.enterpriseId || null}
                    documentType="InternalDncPolicy"
                    label="Upload Internal DNC Policy"
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <BCIDDocumentUpload
                    entityId={enterpriseData?.dto?.enterpriseId || null}
                    documentType="ClientDncPolicies"
                    label="Upload Client DNC Policies (if BPO)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: activeTab === "auth" ? "block" : "none" }}>
          <div className="card mt-3">
            <div className="card-header">
              <i className="bi bi-lock me-2"></i>
              Call Authentication & Technical Controls
            </div>
            <div className="card-body">
              <div className={styles.formGrid}>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="stirShakenCompliance" className="form-label">
                    Are your calls STIR/SHAKEN compliant? If so, who is your
                    call signer?
                  </label>
                  <textarea
                    id="stirShakenCompliance"
                    className="form-control"
                    rows={2}
                    {...register("stirShakenCompliance")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label
                    htmlFor="usesPrerecordedOrAiVoice"
                    className="form-label"
                  >
                    Do you use prerecorded or AI voice?
                  </label>
                  <textarea
                    id="usesPrerecordedOrAiVoice"
                    className="form-control"
                    rows={2}
                    {...register("usesPrerecordedOrAiVoice")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <BCIDDocumentUpload
                    entityId={enterpriseData?.dto?.enterpriseId || null}
                    documentType="SampleRecordings"
                    label="Provide Sample Recordings (if applicable)"
                    accept="audio/*,video/*"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card mt-3">
            <div className="card-header">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Risk & Behavioral Review
            </div>
            <div className="card-body">
              <div className={styles.formGrid}>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="fraudSafeguards" className="form-label">
                    Describe safeguards against fraud/scam usage
                  </label>
                  <textarea
                    id="fraudSafeguards"
                    className="form-control"
                    rows={3}
                    {...register("fraudSafeguards")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label
                    htmlFor="callCampaignMonitoring"
                    className="form-label"
                  >
                    Describe monitoring of call campaigns
                  </label>
                  <textarea
                    id="callCampaignMonitoring"
                    className="form-control"
                    rows={3}
                    {...register("callCampaignMonitoring")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="complaintRates" className="form-label">
                    Provide complaint rates if available
                  </label>
                  <textarea
                    id="complaintRates"
                    className="form-control"
                    rows={2}
                    {...register("complaintRates")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: activeTab === "financial" ? "block" : "none" }}>
          <div className="card mt-3">
            <div className="card-header">
              <i className="bi bi-bank me-2"></i>
              Financial / Credibility Information
            </div>
            <div className="card-body">
              <div className={styles.formGrid}>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="businessReferences" className="form-label">
                    Business References (typically 2)
                  </label>
                  <textarea
                    id="businessReferences"
                    className="form-control"
                    rows={3}
                    {...register("businessReferences")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="bankingReference" className="form-label">
                    Banking / Financial Reference
                  </label>
                  <textarea
                    id="bankingReference"
                    className="form-control"
                    rows={2}
                    {...register("bankingReference")}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <BCIDDocumentUpload
                    entityId={enterpriseData?.dto?.enterpriseId || null}
                    documentType="FinancialStatements"
                    label="Financial Statements or Supporting Documentation (optional)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: activeTab === "contacts" ? "block" : "none" }}>
          <div className="card mt-3">
            <div className="card-header">
              <i className="bi bi-chat-dots me-2"></i>
              Consumer Contact Channels
            </div>
            <div className="card-body">
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label
                    htmlFor="consumerComplaintEmail"
                    className="form-label"
                  >
                    Consumer Complaint Email
                  </label>
                  <input
                    id="consumerComplaintEmail"
                    type="email"
                    className="form-control"
                    {...register("consumerComplaintEmail")}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label
                    htmlFor="consumerComplaintPhone"
                    className="form-label"
                  >
                    Consumer Complaint Phone Number
                  </label>
                  <input
                    id="consumerComplaintPhone"
                    type="tel"
                    className="form-control"
                    {...register("consumerComplaintPhone")}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="privacyPolicyUrl" className="form-label">
                    Privacy Policy URL
                  </label>
                  <input
                    id="privacyPolicyUrl"
                    type="url"
                    className="form-control"
                    {...register("privacyPolicyUrl")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{ display: activeTab === "obligations" ? "block" : "none" }}
        >
          <div className="card mt-3">
            <div className="card-header">
              <i className="bi bi-clipboard-check me-2"></i>
              Ongoing Obligations
            </div>
            <div className="card-body">
              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="mustUpdateInfoChanges"
                  {...register("mustUpdateInfoChanges")}
                />
                <label
                  className="form-check-label"
                  htmlFor="mustUpdateInfoChanges"
                >
                  I understand I must update any changes to my information
                </label>
              </div>
              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="brandingChangesRequireApproval"
                  {...register("brandingChangesRequireApproval")}
                />
                <label
                  className="form-check-label"
                  htmlFor="brandingChangesRequireApproval"
                >
                  I understand branding changes require re-approval
                </label>
              </div>
              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="subjectToRevettingAndAudits"
                  {...register("subjectToRevettingAndAudits")}
                />
                <label
                  className="form-check-label"
                  htmlFor="subjectToRevettingAndAudits"
                >
                  I understand I may be subject to re-vetting and audits
                </label>
              </div>
            </div>
          </div>
          <div className="card mt-3">
            <div className="card-header">
              <i className="bi bi-envelope me-2"></i>
              Notifications
            </div>
            <div className="card-body">
              <h6>Send notifications to:</h6>
              <EmailTextarea name="notificationEmails" control={control} />
              <small className="text-muted">
                Separate multiple email addresses with commas, semicolons, or
                new lines.
              </small>
            </div>
          </div>
        </div>

        <div className="mt-3 d-flex justify-content-between">
          <div className="d-flex gap-2">
            {/* Withdraw: visible when status is Pending Verification (0) */}
            {enterpriseData?.dto && enterpriseData.dto.status == 0 && (
              <button
                type="button"
                className="btn btn-warning"
                onClick={async () => {
                  try {
                    await callApiWithOptions({
                      endpoint: "bcid/enterprise/action",
                      method: HttpMethod.POST,
                      body: {
                        entity: "enterprise",
                        entityId: enterpriseData.dto.enterpriseId,
                        action: "withdraw",
                      },
                    });
                    loadData();
                  } catch (err: any) {
                    setSubmitFeedback(err?.response?.data?.error || "Failed to withdraw submission.");
                  }
                }}
              >
                Withdraw Submission
              </button>
            )}
            {enterpriseData?.dto?.status != 0 && (
              <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  const msg = getDraftConfirmMessage();
                  const doSave = () => {
                    setSavingAction("draft");
                    onSave(true);
                  };
                  if (msg) {
                    setDraftConfirm({ message: msg, onConfirm: doSave });
                  } else {
                    doSave();
                  }
                }}
                disabled={saveInProgress}
              >
                {savingAction === "draft" && saveInProgress ? (
                  <SaveInProgress isVisible={true} />
                ) : (
                  "Save Draft"
                )}
              </button>
            {activeTab !== "obligations" && (
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => {
                  const msg = getDraftConfirmMessage();
                  const doSave = async () => {
                    setSavingAction("continue");
                    const success = await onSave(true);
                    if (success) {
                      const currentIndex = tabOrder.indexOf(activeTab);
                      if (currentIndex < tabOrder.length - 1) {
                        setActiveTab(tabOrder[currentIndex + 1]);
                      }
                    }
                  };
                  if (msg) {
                    setDraftConfirm({ message: msg, onConfirm: doSave });
                  } else {
                    doSave();
                  }
                }}
                disabled={saveInProgress}
              >
                {savingAction === "continue" && saveInProgress ? (
                  <SaveInProgress isVisible={true} />
                ) : (
                  "Save & Continue"
                )}
              </button>
            )}
              </>
            )}
          </div>
          {activeTab === "obligations" && enterpriseData?.dto?.status != 0 && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={async () => {
                setSubmitFeedback(null);
                const v = getValues();
                if (
                  !v.mustUpdateInfoChanges ||
                  !v.brandingChangesRequireApproval ||
                  !v.subjectToRevettingAndAudits
                ) {
                  const missing: string[] = [];
                  if (!v.mustUpdateInfoChanges) {
                    missing.push(
                      "“I understand I must update any changes to my information”",
                    );
                  }
                  if (!v.brandingChangesRequireApproval) {
                    missing.push(
                      "“I understand branding changes require re-approval”",
                    );
                  }
                  if (!v.subjectToRevettingAndAudits) {
                    missing.push(
                      "“I understand I may be subject to re-vetting and audits”",
                    );
                  }
                  setSubmitFeedback(
                    `Submit for vetting requires all three ongoing obligation confirmations. Still need to check: ${missing.join(", ")}.`,
                  );
                  return;
                }
                setSavingAction("submit");
                await onSave(false);
              }}
              disabled={saveInProgress}
            >
              {savingAction === "submit" && saveInProgress ? (
                <SaveInProgress isVisible={true} />
              ) : (
                "Submit for Vetting"
              )}
            </button>
          )}
        </div>
      </form>

      <ConfirmationModal
        isVisible={!!draftConfirm}
        title="Vetting Process"
        message={draftConfirm?.message ?? ""}
        confirmText="Continue"
        confirmVariant="warning"
        onConfirm={() => {
          draftConfirm?.onConfirm();
          setDraftConfirm(null);
        }}
        onCancel={() => setDraftConfirm(null)}
      />
      <ConfirmationModal
        isVisible={unsavedTabTarget !== null}
        title="Unsaved changes"
        message="You have unsaved changes on this tab. Click Save to keep your work, or Discard to undo your changes."
        confirmText="Save"
        cancelText="Discard"
        confirmVariant="primary"
        onCancel={() => {
          void (async () => {
            const target = unsavedTabTarget;
            setUnsavedTabTarget(null);
            await loadData(false);
            if (target) {
              setActiveTab(target);
            }
          })();
        }}
        onConfirm={() => {
          void (async () => {
            const target = unsavedTabTarget;
            setUnsavedTabTarget(null);
            setSavingAction("draft");
            const ok = await onSave(true);
            if (ok && target) {
              setActiveTab(target);
            }
          })();
        }}
      />
    </div>
  );
};

export default ManageEnterprise;
